<script>
import { apiSecond } from '@/boot/axios'
import './updates.css'
import MainContainer from '@/components/main/MainContainer.vue'
import UpdateIcon from '@/components/system-updates/UpdateIcon.vue'
import {
	categoryBadgeStyle,
	formatUpdateDate,
	apiErrorMessage,
} from '@/components/system-updates/utils'

const PER_PAGE = 10
const SEARCH_MAX = 255
const SEARCH_DEBOUNCE = 400

function readQueryId(value) {
	const id = Number(value)
	return Number.isInteger(id) && id > 0 ? id : null
}

// Публичная страница «Обновления системы»: лента с вкладками-категориями, поиском
// и «Показать ещё». «Подробнее» ведёт на страницу обновления /updates/:id (show.vue).
export default {
	name: 'SystemUpdatesPage',
	components: { MainContainer, UpdateIcon },
	data() {
		const query = this.$route.query
		const search = typeof query.search === 'string' ? query.search.slice(0, SEARCH_MAX) : ''
		return {
			coverUrl: require('@/assets/img/system-updates/page-hero.jpg'),
			searchMax: SEARCH_MAX,

			categories: [],
			categoryId: readQueryId(query.category),
			searchInput: search,
			search: search.trim(),

			items: [],
			page: 0,
			lastPage: 1,
			total: 0,
			loading: true,
			loadingMore: false,
			error: null,
			moreError: null,

			brokenPreviews: {},

			requestSeq: 0,
			searchTimer: null,
		}
	},
	computed: {
		hasMore() {
			return this.page > 0 && this.page < this.lastPage
		},
		hasFilters() {
			return Boolean(this.categoryId || this.search)
		},
		activeCategoryExists() {
			return !this.categoryId || this.categories.some((c) => c.id === this.categoryId)
		},
	},
	watch: {
		// Переход по ссылке на эту же страницу (колокольчик, «Назад» в браузере)
		'$route.query'(query) {
			const categoryId = readQueryId(query.category)
			const search =
				typeof query.search === 'string' ? query.search.slice(0, SEARCH_MAX).trim() : ''
			const filtersChanged = categoryId !== this.categoryId || search !== this.search

			if (filtersChanged) {
				this.categoryId = categoryId
				this.search = search
				this.searchInput = search
			}

			if (this.redirectLegacyLink()) return
			if (filtersChanged) this.loadList(true)
		},
		'$i18n.locale'() {
			this.loadCategories()
			this.loadList(true)
		},
	},
	created() {
		this.redirectLegacyLink()
	},
	mounted() {
		this.loadCategories()
		this.loadList(true)
	},
	beforeDestroy() {
		clearTimeout(this.searchTimer)
	},
	methods: {
		categoryBadgeStyle,
		formatUpdateDate,

		async loadCategories() {
			try {
				const { data } = await apiSecond.get('v1/public/system-update-categories')
				this.categories = Array.isArray(data && data.data) ? data.data : []
			} catch (e) {
				// Без вкладок страница всё равно работает: остаётся «Все обновления»
				this.categories = []
			}
		},

		async loadList(reset) {
			const seq = ++this.requestSeq
			const page = reset ? 1 : this.page + 1

			if (reset) {
				this.loading = true
				this.error = null
				this.moreError = null
			} else {
				this.loadingMore = true
				this.moreError = null
			}

			const params = { page, per_page: PER_PAGE }
			if (this.categoryId) params.category_id = this.categoryId
			if (this.search) params.search = this.search

			try {
				const { data } = await apiSecond.get('v1/public/system-updates', { params })
				if (seq !== this.requestSeq) return

				const list = Array.isArray(data && data.data) ? data.data : []
				if (reset) {
					this.items = list
				} else {
					// Между страницами могли опубликовать новое — без дублей по id
					const known = new Set(this.items.map((item) => item.id))
					this.items = this.items.concat(list.filter((item) => !known.has(item.id)))
				}
				this.page = Number(data.current_page) || page
				this.lastPage = Number(data.last_page) || this.page
				this.total = Number(data.total) || this.items.length
			} catch (error) {
				if (seq !== this.requestSeq) return
				const message = apiErrorMessage(error, this.$t('system_updates.load_error'))
				if (reset) {
					this.items = []
					this.page = 0
					this.lastPage = 1
					this.error = message
				} else {
					this.moreError = message
				}
				return
			} finally {
				if (seq === this.requestSeq) {
					this.loading = false
					this.loadingMore = false
				}
			}
		},

		loadMore() {
			if (this.loadingMore || this.loading || !this.hasMore) return
			this.loadList(false)
		},

		detailLink(item) {
			return '/' + this.$i18n.locale + '/updates/' + item.id
		},

		// Ссылки вида /updates?id=N (раньше раскрывали карточку) ведут на страницу обновления
		redirectLegacyLink() {
			const id = readQueryId(this.$route.query.id)
			if (!id) return false
			this.$router.replace('/' + this.$i18n.locale + '/updates/' + id).catch(() => {})
			return true
		},

		updateQuery(patch) {
			const query = { ...this.$route.query, ...patch }
			Object.keys(query).forEach((key) => {
				if (query[key] === undefined || query[key] === null || query[key] === '') delete query[key]
			})
			this.$router.replace({ query }).catch(() => {})
		},

		selectCategory(id) {
			if (id === this.categoryId) return
			this.updateQuery({ category: id ? String(id) : undefined })
		},

		onSearchInput() {
			clearTimeout(this.searchTimer)
			this.searchTimer = setTimeout(this.applySearch, SEARCH_DEBOUNCE)
		},

		applySearch() {
			clearTimeout(this.searchTimer)
			const value = (this.searchInput || '').slice(0, SEARCH_MAX).trim()
			if (value === this.search) return
			this.updateQuery({ search: value || undefined })
		},

		clearSearch() {
			this.searchInput = ''
			this.applySearch()
		},

		resetFilters() {
			clearTimeout(this.searchTimer)
			this.searchInput = ''
			this.updateQuery({ category: undefined, search: undefined })
		},

		sections(item) {
			return (Array.isArray(item.highlights) ? item.highlights : []).filter((h) => h && h.title)
		},

		previewIcon(item) {
			const first = this.sections(item)[0]
			return (first && first.icon) || 'document'
		},

		showPreviewImage(item) {
			return Boolean(item.preview_url) && !this.brokenPreviews[item.id]
		},
	},
	head: {
		title() {
			return {
				inner: this.$t('system_updates.title'),
			}
		},
		meta: function () {
			return [{ name: 'description', content: this.$t('system_updates.hero_text') }]
		},
	},
}
</script>

<template>
	<div class="wrap__content sup">
		<MainContainer>
			<section class="sup-hero">
				<img class="sup-hero__bg" :src="coverUrl" alt="" />
				<div class="sup-hero__shade"></div>
				<div class="sup-hero__content">
					<h1 class="sup-hero__title">{{ $t('system_updates.hero_title') }}</h1>
					<p class="sup-hero__text">{{ $t('system_updates.hero_text') }}</p>
				</div>
			</section>

			<div class="sup-follow">
				<span class="sup-follow__icon">
					<svg
						width="26"
						height="26"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="1.8"
						stroke-linecap="round"
						stroke-linejoin="round"
					>
						<path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1z" />
						<path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" />
					</svg>
				</span>
				<div class="sup-follow__text">
					<div class="sup-follow__title">{{ $t('system_updates.follow_title') }}</div>
					<div class="sup-follow__desc">{{ $t('system_updates.follow_text') }}</div>
				</div>
			</div>

			<div class="sup-toolbar">
				<div class="sup-tabs" role="tablist">
					<button
						type="button"
						role="tab"
						class="sup-tab"
						:class="{ 'is-active': !categoryId || !activeCategoryExists }"
						:aria-selected="String(!categoryId)"
						@click="selectCategory(null)"
					>
						{{ $t('system_updates.all') }}
					</button>
					<button
						v-for="category in categories"
						:key="category.id"
						type="button"
						role="tab"
						class="sup-tab"
						:class="{ 'is-active': categoryId === category.id }"
						:aria-selected="String(categoryId === category.id)"
						@click="selectCategory(category.id)"
					>
						{{ category.name }}
					</button>
				</div>

				<label class="sup-search">
					<svg
						class="sup-search__icon"
						width="18"
						height="18"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
					>
						<circle cx="11" cy="11" r="7" />
						<path d="m20 20-3.6-3.6" />
					</svg>
					<input
						v-model="searchInput"
						type="search"
						class="sup-search__input"
						:maxlength="searchMax"
						:placeholder="$t('system_updates.search')"
						:aria-label="$t('system_updates.search')"
						@input="onSearchInput"
						@keydown.enter.prevent="applySearch"
					/>
					<button
						v-if="searchInput"
						type="button"
						class="sup-search__clear"
						:aria-label="$t('system_updates.clear_search')"
						@click="clearSearch"
					>
						<svg
							width="16"
							height="16"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2.2"
							stroke-linecap="round"
						>
							<path d="M6 6l12 12M18 6 6 18" />
						</svg>
					</button>
				</label>
			</div>

			<div v-if="loading" class="sup-list" aria-busy="true">
				<div v-for="n in 3" :key="n" class="sup-card sup-card--skeleton">
					<span class="sup-card__dot"></span>
					<div class="sup-card__main">
						<div class="sup-skel sup-skel--title"></div>
						<div class="sup-skel sup-skel--meta"></div>
						<div class="sup-skel sup-skel--line"></div>
						<div class="sup-skel sup-skel--line sup-skel--short"></div>
					</div>
					<div class="sup-card__preview sup-skel"></div>
				</div>
			</div>

			<div v-else-if="error" class="sup-state">
				<div class="sup-state__title">{{ error }}</div>
				<button type="button" class="sup-btn sup-btn--primary" @click="loadList(true)">
					{{ $t('system_updates.retry') }}
				</button>
			</div>

			<div v-else-if="!items.length" class="sup-state">
				<div class="sup-state__icon">
					<UpdateIcon :name="hasFilters ? 'search' : 'bell'" :size="28" />
				</div>
				<div class="sup-state__title">
					{{ hasFilters ? $t('system_updates.not_found') : $t('system_updates.empty') }}
				</div>
				<button
					v-if="hasFilters"
					type="button"
					class="sup-btn sup-btn--ghost"
					@click="resetFilters"
				>
					{{ $t('system_updates.reset') }}
				</button>
			</div>

			<div v-else class="sup-list">
				<article
					v-for="item in items"
					:id="'update-' + item.id"
					:key="item.id"
					class="sup-card"
					:class="{ 'is-new': item.is_new }"
				>
					<span class="sup-card__dot"></span>

					<div class="sup-card__main">
						<h2 class="sup-card__title">
							<router-link :to="detailLink(item)" class="sup-card__title-link">{{
								item.title
							}}</router-link>
						</h2>

						<div class="sup-card__meta">
							<span v-if="item.is_new" class="sup-badge sup-badge--new">
								{{ $t('system_updates.new') }}
							</span>
							<span
								v-if="item.category && item.category.name"
								class="sup-badge"
								:style="categoryBadgeStyle(item.category)"
							>
								{{ item.category.name }}
							</span>
							<time v-if="item.published_at" class="sup-card__date" :datetime="item.published_at">
								{{ formatUpdateDate(item.published_at) }}
							</time>
						</div>

						<p v-if="item.summary" class="sup-card__summary">
							{{ item.summary }}
						</p>

						<router-link
							:to="detailLink(item)"
							class="sup-btn sup-card__toggle"
							:class="item.is_new ? 'sup-btn--primary' : 'sup-btn--ghost'"
						>
							{{ $t('system_updates.more') }}
							<svg
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2.2"
								stroke-linecap="round"
								stroke-linejoin="round"
							>
								<path d="M5 12h14M13 6l6 6-6 6" />
							</svg>
						</router-link>
					</div>

					<div class="sup-card__preview" :class="{ 'has-image': showPreviewImage(item) }">
						<img
							v-if="showPreviewImage(item)"
							:src="item.preview_url"
							alt=""
							loading="lazy"
							@error="$set(brokenPreviews, item.id, true)"
						/>
						<span v-else class="sup-card__preview-icon">
							<UpdateIcon :name="previewIcon(item)" :size="40" />
						</span>
					</div>
				</article>

				<div v-if="moreError" class="sup-more-error">
					<span>{{ moreError }}</span>
					<button type="button" class="sup-link" @click="loadMore">
						{{ $t('system_updates.retry') }}
					</button>
				</div>

				<div v-if="hasMore" class="sup-more">
					<button
						type="button"
						class="sup-btn sup-btn--ghost sup-more__btn"
						:disabled="loadingMore"
						@click="loadMore"
					>
						<span v-if="loadingMore" class="sup-spinner" aria-hidden="true"></span>
						{{ $t('system_updates.show_more') }}
						<svg
							v-if="!loadingMore"
							width="16"
							height="16"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2.2"
							stroke-linecap="round"
							stroke-linejoin="round"
						>
							<path d="M12 5v14M6 13l6 6 6-6" />
						</svg>
					</button>
				</div>
			</div>
		</MainContainer>
	</div>
</template>
